import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Post, User, Comment } from '../types';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { 
  getPostById, 
  likePost, 
  unlikePost, 
  createComment, 
  sharePost,
  getUserById,
  getCommentsByPostId,
  likeComment,
  unlikeComment,
  deletePost
} from '../services/firebaseService';
import { LoadingScreen } from '../components/UI/LoadingScreen';
import { isGifUrl } from '../utils/gifUtils';
import LinkEnhancedContent from '../components/UI/LinkEnhancedContent';
import GifPicker from '../components/UI/GifPicker';

export const PostDetail: React.FC = () => {
  const { postId, communityId } = useParams<{ postId: string; communityId?: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [post, setPost] = useState<Post | null>(null);
  const [author, setAuthor] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [sharesCount, setSharesCount] = useState(0);
  const [isLiking, setIsLiking] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [commentText, setCommentText] = useState('');
  const [isCommenting, setIsCommenting] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentAuthors, setCommentAuthors] = useState<{[authorId: string]: User}>({});
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [selectedCommentGif, setSelectedCommentGif] = useState<string>('');
  const [isCommentGifPickerOpen, setIsCommentGifPickerOpen] = useState(false);
  const [commentLikes, setCommentLikes] = useState<{[commentId: string]: {isLiked: boolean, count: number}}>({});
  
  // États pour les réponses aux commentaires
  const [replyingTo, setReplyingTo] = useState<string | null>(null); // ID du commentaire auquel on répond
  const [replyText, setReplyText] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [selectedReplyGif, setSelectedReplyGif] = useState<string>('');
  const [isReplyGifPickerOpen, setIsReplyGifPickerOpen] = useState(false);
  const [showReplies, setShowReplies] = useState<{[commentId: string]: boolean}>({});

  const categoryEmojis = {
    general: '💬',
    feedback: '📝',
    meme: '😂',
    research: '🔬',
    launchpad: '🚀',
    dao: '🏛️',
    annonces: '📢',
  };

  const convertFirestoreDate = (date: any): Date => {
    if (!date) return new Date();
    if (date instanceof Date) return date;
    if (date.toDate && typeof date.toDate === 'function') return date.toDate();
    return new Date(date);
  };

  const formatTimeAgo = (date: Date | any) => {
    if (!date) return 'Maintenant';
    
    // Convertir en objet Date si nécessaire
    let dateObj: Date;
    if (date instanceof Date) {
      dateObj = date;
    } else if (date.toDate && typeof date.toDate === 'function') {
      // Firestore Timestamp
      dateObj = date.toDate();
    } else if (typeof date === 'string' || typeof date === 'number') {
      dateObj = new Date(date);
    } else {
      return 'Maintenant';
    }
    
    // Vérifier si la date est valide
    if (isNaN(dateObj.getTime())) {
      return 'Maintenant';
    }
    
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);
    
    if (diffInSeconds < 60) return `${Math.max(0, diffInSeconds)}s`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}j`;
    
    return dateObj.toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  useEffect(() => {
    const loadPost = async () => {
      if (!postId) {
        setError('ID de post manquant');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        let postData: Post | null = null;

        // Déterminer si c'est un post communautaire ou général
        if (communityId) {
          // C'est un post communautaire
          const communityPostDoc = await getDoc(doc(db, 'community_posts', postId));
          if (communityPostDoc.exists()) {
            const data = communityPostDoc.data();
            postData = {
              id: communityPostDoc.id,
              ...data,
              createdAt: convertFirestoreDate(data.createdAt),
              updatedAt: convertFirestoreDate(data.updatedAt),
            } as Post;
          }
        } else {
          // C'est un post général
          postData = await getPostById(postId);
        }

        if (postData) {
          setPost(postData);
          setLikesCount(postData.likes.length);
          setSharesCount(postData.shares || 0);
          setIsLiked(user ? postData.likes.includes(user.id) : false);
          
          // Charger l'auteur
          const authorData = await getUserById(postData.authorId);
          if (authorData) {
            setAuthor(authorData);
          }
          
          // Charger les commentaires
          await loadComments(postData.id);
        } else {
          setError('Post non trouvé');
        }
      } catch (err) {
        console.error('Erreur lors du chargement du post:', err);
        setError('Erreur lors du chargement du post');
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [postId, communityId, user]);

  // Fermer le menu d'options en cliquant ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showOptionsMenu) {
        setShowOptionsMenu(false);
      }
    };

    if (showOptionsMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showOptionsMenu]);

  const loadComments = async (postId: string) => {
    try {
      setIsLoadingComments(true);
      const commentsData = await getCommentsByPostId(postId);
      
      // Convertir les dates des commentaires
      const commentsWithDates = commentsData.map(comment => ({
        ...comment,
        createdAt: convertFirestoreDate(comment.createdAt),
        updatedAt: convertFirestoreDate(comment.updatedAt),
      }));
      
      // Organiser les commentaires en structure hiérarchique
      const commentsMap = new Map<string, Comment>();
      const topLevelComments: Comment[] = [];
      
      // D'abord, créer une map de tous les commentaires
      commentsWithDates.forEach(comment => {
        commentsMap.set(comment.id, { ...comment, replies: [] });
      });
      
      // Ensuite, organiser la hiérarchie
      commentsWithDates.forEach(comment => {
        if (comment.parentId) {
          // C'est une réponse
          const parentComment = commentsMap.get(comment.parentId);
          if (parentComment) {
            if (!parentComment.replies) parentComment.replies = [];
            parentComment.replies.push(commentsMap.get(comment.id)!);
          }
        } else {
          // C'est un commentaire principal
          topLevelComments.push(commentsMap.get(comment.id)!);
        }
      });
      
      // Trier les commentaires et leurs réponses par date
      topLevelComments.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      topLevelComments.forEach(comment => {
        if (comment.replies) {
          comment.replies.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        }
      });
      
      setComments(topLevelComments);
      
      // Initialiser les états des likes pour tous les commentaires (principaux et réponses)
      const likesState: {[commentId: string]: {isLiked: boolean, count: number}} = {};
      const initializeLikes = (comments: Comment[]) => {
        comments.forEach(comment => {
          likesState[comment.id] = {
            isLiked: user ? comment.likes.includes(user.id) : false,
            count: comment.likes.length
          };
          if (comment.replies) {
            initializeLikes(comment.replies);
          }
        });
      };
      initializeLikes(topLevelComments);
      setCommentLikes(likesState);
      
      // Charger les auteurs des commentaires
      const authorIds = [...new Set(commentsData.map(comment => comment.authorId))];
      const authors: {[authorId: string]: User} = {};
      
      await Promise.all(authorIds.map(async (authorId) => {
        const authorData = await getUserById(authorId);
        if (authorData) {
          authors[authorId] = authorData;
        }
      }));
      
      setCommentAuthors(authors);
    } catch (error) {
      console.error('Erreur lors du chargement des commentaires:', error);
    } finally {
      setIsLoadingComments(false);
    }
  };

  const handleBackClick = () => {
    navigate(-1); // Retour à la page précédente
  };

  const handleLike = async () => {
    if (!user || !post || isLiking) return;

    setIsLiking(true);
    try {
      if (isLiked) {
        await unlikePost(post.id, user.id);
        setIsLiked(false);
        setLikesCount(prev => prev - 1);
      } else {
        await likePost(post.id, user.id);
        setIsLiked(true);
        setLikesCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Erreur lors du like:', error);
    } finally {
      setIsLiking(false);
    }
  };

  const handleShare = async () => {
    if (!post) return;

    try {
      const postUrl = `${window.location.origin}/post/${post.id}`;
      
      if (navigator.share) {
        await navigator.share({
          title: 'Jupipotes - Post',
          text: post.content.substring(0, 100) + (post.content.length > 100 ? '...' : ''),
          url: postUrl,
        });
      } else {
        await navigator.clipboard.writeText(postUrl);
        // Afficher une notification temporaire
        const notification = document.createElement('div');
        notification.className = 'fixed top-4 right-4 bg-emerald-700 text-emerald-100 px-4 py-2 rounded-lg shadow-lg z-[70] cosmic-card';
        notification.textContent = 'Lien copié dans le presse-papiers !';
        document.body.appendChild(notification);
        
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 3000);
      }
      
      if (user) {
        await sharePost(post.id);
        setSharesCount(prev => prev + 1);
      }
    } catch (error) {
      console.log('Partage annulé ou erreur:', error);
    }
  };

  const handleDelete = async () => {
    if (!user || !post || post.authorId !== user.id) return;
    
    const confirmed = window.confirm('Êtes-vous sûr de vouloir supprimer ce post ? Cette action est irréversible.');
    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await deletePost(post.id);
      
      // Notification de succès
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-emerald-700 text-emerald-100 px-4 py-2 rounded-lg shadow-lg z-50 cosmic-card';
      notification.textContent = 'Post supprimé avec succès !';
      document.body.appendChild(notification);
      
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 3000);

      // Rediriger vers la page d'accueil après suppression
      navigate('/');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression du post');
    } finally {
      setIsDeleting(false);
      setShowOptionsMenu(false);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !post || (!commentText.trim() && !selectedCommentGif) || isCommenting) return;

    setIsCommenting(true);
    try {
      const content = selectedCommentGif || commentText.trim();
      await createComment({
        postId: post.id,
        authorId: user.id,
        content,
        likes: [],
        isDeleted: false
      });

      setCommentText('');
      setSelectedCommentGif('');
      await loadComments(post.id);
    } catch (error) {
      console.error('Erreur lors du commentaire:', error);
    } finally {
      setIsCommenting(false);
    }
  };

  const handleCommentGifSelect = (gifUrl: string) => {
    setSelectedCommentGif(gifUrl);
    setIsCommentGifPickerOpen(false);
  };

  const handleReplyGifSelect = (gifUrl: string) => {
    setSelectedReplyGif(gifUrl);
    setIsReplyGifPickerOpen(false);
  };

  const handleStartReply = (commentId: string, authorName: string) => {
    setReplyingTo(commentId);
    setReplyText(`@${authorName} `);
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setReplyText('');
    setSelectedReplyGif('');
  };

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !post || !replyingTo || (!replyText.trim() && !selectedReplyGif) || isReplying) return;

    setIsReplying(true);
    try {
      const content = selectedReplyGif || replyText.trim();
      await createComment({
        postId: post.id,
        authorId: user.id,
        content,
        likes: [],
        isDeleted: false,
        parentId: replyingTo
      });

      setReplyText('');
      setSelectedReplyGif('');
      setReplyingTo(null);
      await loadComments(post.id);
    } catch (error) {
      console.error('Erreur lors de la réponse:', error);
    } finally {
      setIsReplying(false);
    }
  };

  const toggleReplies = (commentId: string) => {
    setShowReplies(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  };

  const handleCommentLike = async (commentId: string) => {
    if (!user || !post) return;

    try {
      const currentState = commentLikes[commentId];
      if (currentState.isLiked) {
        await unlikeComment(post.id, commentId, user.id);
        setCommentLikes(prev => ({
          ...prev,
          [commentId]: {
            isLiked: false,
            count: prev[commentId].count - 1
          }
        }));
      } else {
        await likeComment(post.id, commentId, user.id);
        setCommentLikes(prev => ({
          ...prev,
          [commentId]: {
            isLiked: true,
            count: prev[commentId].count + 1
          }
        }));
      }
    } catch (error) {
      console.error('Erreur lors du like du commentaire:', error);
    }
  };

  const handleUserClick = (userId: string) => {
    navigate(`/profile/${userId}`);
  };

  const renderImages = () => {
    if (!post?.mediaUrls || post.mediaUrls.length === 0) return null;

    const imageCount = post.mediaUrls.length;

    // Gestion spéciale pour les GIFs
    if (imageCount === 1 && isGifUrl(post.mediaUrls[0])) {
      return (
        <div className="mt-6">
          <img
            src={post.mediaUrls[0]}
            alt="GIF"
            className="w-full max-h-96 object-cover rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => setSelectedImageIndex(0)}
          />
        </div>
      );
    }

    if (imageCount === 1) {
      return (
        <div className="mt-6">
          <img
            src={post.mediaUrls[0]}
            alt="Post media"
            className="w-full max-h-96 object-cover rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => setSelectedImageIndex(0)}
          />
        </div>
      );
    }

    if (imageCount === 2) {
      return (
        <div className="mt-6 grid grid-cols-2 gap-3">
          {post.mediaUrls.map((url, index) => (
            <img
              key={url + '-' + index}
              src={url}
              alt={`Post media ${index + 1}`}
              className="w-full h-48 object-cover rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => setSelectedImageIndex(index)}
            />
          ))}
        </div>
      );
    }

    if (imageCount === 3) {
      return (
        <div className="mt-6 grid grid-cols-2 gap-3">
          <img
            src={post.mediaUrls[0]}
            alt="Post media 1"
            className="w-full h-48 object-cover rounded-xl cursor-pointer hover:opacity-90 transition-opacity row-span-2"
            onClick={() => setSelectedImageIndex(0)}
          />
          <div className="grid grid-rows-2 gap-3">
            {post.mediaUrls.slice(1).map((url, index) => (
              <img
                key={url + '-' + (index + 1)}
                src={url}
                alt={`Post media ${index + 2}`}
                className="w-full h-24 object-cover rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => setSelectedImageIndex(index + 1)}
              />
            ))}
          </div>
        </div>
      );
    }

    if (imageCount >= 4) {
      return (
        <div className="mt-6 grid grid-cols-2 gap-3">
          {post.mediaUrls.slice(0, 3).map((url, index) => (
            <img
              key={url + '-' + index}
              src={url}
              alt={`Post media ${index + 1}`}
              className="w-full h-32 object-cover rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => setSelectedImageIndex(index)}
            />
          ))}
          <div
            className="relative w-full h-32 bg-gray-700 rounded-xl cursor-pointer hover:bg-gray-600 transition-colors flex items-center justify-center"
            onClick={() => setSelectedImageIndex(3)}
          >
            <img
              src={post.mediaUrls[3]}
              alt="Post media 4"
              className="w-full h-full object-cover rounded-xl opacity-60"
            />
            {imageCount > 4 && (
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-xl flex items-center justify-center">
                <span className="text-white text-xl font-bold">+{imageCount - 3}</span>
              </div>
            )}
          </div>
        </div>
      );
    }
  };

  // Fonction récursive pour rendre les commentaires et leurs réponses
  const renderComment = (comment: Comment, depth: number = 0) => {
    const commentAuthor = commentAuthors[comment.authorId];
    const isReplyFormOpen = replyingTo === comment.id;
    const hasReplies = comment.replies && comment.replies.length > 0;
    const areRepliesVisible = showReplies[comment.id];
    
    return (
      <div key={comment.id} className={`${depth > 0 ? 'ml-8 mt-3' : ''}`}>
        <div className="cosmic-card p-4">
          <div className="flex space-x-3">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-emerald-700/50 flex-shrink-0">
              <img
                src={commentAuthor?.profilePicture || `https://api.dicebear.com/7.x/shapes/svg?seed=${comment.authorId}`}
                alt={commentAuthor?.displayName || commentAuthor?.username || 'Utilisateur'}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1">
              <div className="bg-gray-800/70 rounded-lg p-3 border border-emerald-800/30 backdrop-blur-sm">
                <div className="flex items-center space-x-2 mb-1">
                  <span 
                    className="font-semibold text-emerald-100 text-sm cursor-pointer hover:text-emerald-200 transition-colors"
                    onClick={() => handleUserClick(comment.authorId)}
                  >
                    {commentAuthor?.displayName || commentAuthor?.username || `Utilisateur ${comment.authorId.slice(-4)}`}
                  </span>
                  {commentAuthor?.isVerified && (
                    <svg className="w-3 h-3 text-cyan-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  )}
                  <span className="text-xs text-emerald-300/60">
                    {formatTimeAgo(comment.createdAt || new Date())}
                  </span>
                </div>
                <div className="text-emerald-100 text-sm">
                  {isGifUrl(comment.content) ? (
                    <img src={comment.content} alt="GIF" className="max-w-48 rounded-lg" />
                  ) : (
                    <LinkEnhancedContent content={comment.content} />
                  )}
                </div>
                
                {/* Actions du commentaire */}
                <div className="flex items-center space-x-4 mt-2 pt-2 border-t border-emerald-800/20">
                  {user ? (
                    <button
                      onClick={() => handleCommentLike(comment.id)}
                      className={`flex items-center space-x-1 text-xs transition-colors ${
                        commentLikes[comment.id]?.isLiked 
                          ? 'text-red-400 hover:text-red-300' 
                          : 'text-emerald-300/60 hover:text-red-400'
                      }`}
                    >
                      <svg className="w-4 h-4" fill={commentLikes[comment.id]?.isLiked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      <span>{commentLikes[comment.id]?.count || comment.likes.length}</span>
                    </button>
                  ) : (
                    <div className="flex items-center space-x-1 text-xs text-emerald-300/60">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      <span>{comment.likes.length}</span>
                    </div>
                  )}
                  
                  {user && depth < 3 && (
                    <button 
                      onClick={() => handleStartReply(comment.id, commentAuthor?.username || commentAuthor?.displayName || 'user')}
                      className="flex items-center space-x-1 text-xs text-emerald-300/60 hover:text-emerald-300 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                      </svg>
                      <span>Répondre</span>
                    </button>
                  )}
                  
                  {hasReplies && (
                    <button 
                      onClick={() => toggleReplies(comment.id)}
                      className="flex items-center space-x-1 text-xs text-cyan-300/60 hover:text-cyan-300 transition-colors"
                    >
                      <svg className={`w-4 h-4 transition-transform ${areRepliesVisible ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                      <span>{areRepliesVisible ? 'Masquer' : 'Voir'} {comment.replies!.length} réponse{comment.replies!.length > 1 ? 's' : ''}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Formulaire de réponse */}
        {isReplyFormOpen && user && (
          <div className="ml-8 mt-3">
            <div className="cosmic-card p-4">
              <form onSubmit={handleSubmitReply}>
                <div className="flex space-x-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-emerald-700 to-cyan-600 flex items-center justify-center border border-cyan-400/30 overflow-hidden flex-shrink-0">
                    {user.profilePicture ? (
                      <img
                        src={user.profilePicture}
                        alt={user.displayName || user.username || 'User'}
                        className="w-full h-full object-cover rounded-full"
                      />
                    ) : (
                      <span className="text-emerald-100 font-bold text-xs">
                        {user.displayName?.charAt(0) || 'U'}
                      </span>
                    )}
                  </div>
                  <div className="flex-1">
                    {selectedReplyGif && (
                      <div className="relative inline-block mb-2">
                        <img 
                          src={selectedReplyGif} 
                          alt="GIF sélectionné" 
                          className="max-w-32 max-h-32 rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => setSelectedReplyGif('')}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                        >
                          ×
                        </button>
                      </div>
                    )}
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Écrivez votre réponse..."
                      className="w-full p-2 rounded-lg bg-emerald-900/20 border border-emerald-800/30 text-emerald-100 placeholder-emerald-300/60 focus:outline-none focus:border-emerald-600 resize-none text-sm"
                      rows={2}
                    />
                    <div className="flex items-center justify-between mt-2">
                      <button
                        type="button"
                        onClick={() => setIsReplyGifPickerOpen(!isReplyGifPickerOpen)}
                        className="p-1 rounded hover:bg-emerald-800/30 transition-colors text-emerald-300/80 hover:text-emerald-200"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m0 0V1h-1m1 3h4a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2h4m0-3v3m0 0h8" />
                        </svg>
                      </button>
                      
                      <div className="space-x-2">
                        <button
                          type="button"
                          onClick={handleCancelReply}
                          className="text-xs px-3 py-1 rounded text-emerald-300/80 hover:text-emerald-200 transition-colors"
                        >
                          Annuler
                        </button>
                        <button
                          type="submit"
                          disabled={(!replyText.trim() && !selectedReplyGif) || isReplying}
                          className="cosmic-button text-xs px-3 py-1 disabled:opacity-50"
                        >
                          {isReplying ? 'Envoi...' : 'Répondre'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Réponses */}
        {hasReplies && areRepliesVisible && (
          <div className="mt-2">
            {comment.replies!.map(reply => renderComment(reply, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <LoadingScreen isLoading={true} onComplete={() => {}} />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="cosmic-card p-8 text-center max-w-md">
          <svg className="w-16 h-16 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h2 className="text-xl font-semibold text-emerald-100 mb-2">Erreur</h2>
          <p className="text-emerald-200/80 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="cosmic-button px-6 py-2"
          >
            Retourner à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (!post || !author) return null;

  return (
    <div className="min-h-screen">
      <div className="container mx-auto max-w-2xl px-4 py-6">
        {/* Header avec bouton retour */}
        <div className="flex items-center mb-6">
          <button
            onClick={handleBackClick}
            className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-emerald-800/30 transition-colors mr-4"
          >
            <svg className="w-5 h-5 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-semibold text-emerald-100">Publication</h1>
        </div>

        {/* Contenu principal du post */}
        <div className="cosmic-card p-6">
          {/* Header du post */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div 
                className="w-12 h-12 rounded-full bg-gradient-to-r from-emerald-700 to-cyan-600 flex items-center justify-center border border-cyan-400/30 animate-float overflow-hidden cursor-pointer"
                onClick={() => handleUserClick(author.id)}
              >
                {author.profilePicture ? (
                  <img
                    src={author.profilePicture}
                    alt={author.displayName || author.username || 'User'}
                    className="w-full h-full object-cover rounded-full"
                  />
                ) : (
                  <span className="text-emerald-100 font-bold">
                    {author.displayName?.charAt(0) || 'U'}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <span 
                    className="font-semibold text-emerald-100 hover:text-emerald-200 cursor-pointer transition-colors"
                    onClick={() => handleUserClick(author.id)}
                  >
                    {author.displayName}
                  </span>
                  {author.isVerified && (
                    <svg className="w-4 h-4 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="flex items-center space-x-2 text-sm text-emerald-300/80">
                  <span>@{author.username || 'jupiter_user'}</span>
                  <span>•</span>
                  <span>{formatTimeAgo(post.createdAt || new Date())}</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-lg">{categoryEmojis[post.category]}</span>
              <span className="text-sm text-emerald-300/80 capitalize">{post.category}</span>
              {/* Badge Thread */}
              {post.isThread && (
                <>
                  <span>•</span>
                  <span className="flex items-center space-x-1 bg-cyan-900/40 border border-cyan-500/30 px-2 py-1 rounded-full">
                    <svg className="w-3 h-3 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-xs font-medium text-cyan-300">
                      {post.threadLength || 1} {(post.threadLength || 1) > 1 ? 'parties' : 'partie'}
                    </span>
                  </span>
                </>
              )}
              
              {/* Options Menu - Only for post author */}
              {user && post.authorId === user.id && (
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowOptionsMenu(!showOptionsMenu);
                    }}
                    className="p-2 rounded-full text-emerald-300/60 hover:text-emerald-300 hover:bg-emerald-900/30 transition-colors"
                    disabled={isDeleting}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01" />
                    </svg>
                  </button>
                  
                  {showOptionsMenu && (
                    <div className="absolute right-0 top-full mt-1 bg-slate-800 border border-emerald-700/30 rounded-lg shadow-lg z-50 min-w-[120px]">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete();
                        }}
                        disabled={isDeleting}
                        className="w-full px-3 py-2 text-left text-red-400 hover:bg-red-900/20 transition-colors flex items-center space-x-2 disabled:opacity-50"
                      >
                        {isDeleting ? (
                          <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                        <span className="text-sm">{isDeleting ? 'Suppression...' : 'Supprimer'}</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Contenu du post avec liens enrichis */}
          <div className="mb-6">
            <LinkEnhancedContent content={
              post.content || 
              // Fallback pour les anciens threads mal formatés
              (post.isThread && post.threadParts && post.threadParts.length > 0 ? post.threadParts[0].content : '')
            } />
          </div>

          {/* Thread parts - Affichage complet du thread */}
          {post.isThread && post.threadParts && post.threadParts.length > 0 && (
            <div className="mb-6 space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-cyan-300 font-medium">Thread complet ({post.threadParts.length + (post.content ? 1 : 0)} {(post.threadParts.length + (post.content ? 1 : 0)) > 1 ? 'parties' : 'partie'})</span>
              </div>
              {/* Afficher les threadParts en ajustant l'index selon si post.content existe */}
              {post.threadParts.map((part, index) => {
                // Si post.content est vide, alors threadParts[0] est déjà affiché en haut, on commence à index 1
                const shouldSkipFirst = !post.content;
                if (shouldSkipFirst && index === 0) return null;
                
                const displayIndex = shouldSkipFirst ? index : index + 1;
                const totalParts = post.threadParts!.length + (post.content ? 1 : 0);
                
                return (
                  <div key={part.id} className="border-l-2 border-cyan-500/30 pl-4 ml-2">
                    <div className="flex items-center space-x-2 mb-3">
                      <span className="text-sm bg-cyan-900/60 text-cyan-200 px-3 py-1 rounded-full border border-cyan-500/50 font-medium">
                        {displayIndex + 1}/{totalParts}
                      </span>
                      <span className="text-xs text-cyan-400/70">Partie du thread</span>
                    </div>
                    <div className="text-emerald-100">
                      <LinkEnhancedContent content={part.content} />
                    </div>
                    {part.mediaUrls && part.mediaUrls.length > 0 && (
                      <div className="mt-3 grid gap-2">
                        {part.mediaUrls.map((url, mediaIndex) => (
                          <img
                            key={`${part.id}-media-${mediaIndex}`}
                            src={url}
                            alt={`Thread ${displayIndex + 1} media ${mediaIndex + 1}`}
                            className="max-w-md rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => setSelectedImageIndex(mediaIndex)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Images */}
          {renderImages()}

          {/* Stats et actions du post */}
          <div className="flex items-center justify-between py-4 border-t border-emerald-800/30 mt-6">
            <div className="flex items-center space-x-6">
              <button
                onClick={handleLike}
                disabled={isLiking}
                className={`flex items-center space-x-2 transition-colors cosmic-hover-effect ${
                  isLiked 
                    ? 'text-red-400 hover:text-red-300' 
                    : 'text-emerald-300/80 hover:text-red-400'
                }`}
              >
                <svg className="w-6 h-6" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <span>{likesCount}</span>
              </button>

              <div className="flex items-center space-x-2 text-emerald-300/80">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span>{comments.length}</span>
              </div>

              <button 
                className="flex items-center space-x-2 text-emerald-300/80 hover:text-emerald-300 transition-colors cosmic-hover-effect"
                onClick={handleShare}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                </svg>
                <span>{sharesCount}</span>
              </button>
            </div>

            <div className="text-sm text-emerald-300/60">
              {post.views} vues
            </div>
          </div>
        </div>

        {/* Section des commentaires */}
        <div className="mt-6">
          {/* Formulaire de commentaire */}
          {user && (
            <div className="cosmic-card p-4 mb-6">
              <form onSubmit={handleSubmitComment}>
                <div className="flex space-x-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-emerald-700 to-cyan-600 flex items-center justify-center border border-cyan-400/30 overflow-hidden flex-shrink-0">
                    {user.profilePicture ? (
                      <img
                        src={user.profilePicture}
                        alt={user.displayName || user.username || 'User'}
                        className="w-full h-full object-cover rounded-full"
                      />
                    ) : (
                      <span className="text-emerald-100 font-bold text-sm">
                        {user.displayName?.charAt(0) || 'U'}
                      </span>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="space-y-3">
                      {selectedCommentGif && (
                        <div className="relative inline-block">
                          <img 
                            src={selectedCommentGif} 
                            alt="GIF sélectionné" 
                            className="max-w-40 max-h-40 rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => setSelectedCommentGif('')}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                          >
                            ×
                          </button>
                        </div>
                      )}
                      <textarea
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder="Écrivez votre commentaire..."
                        className="w-full p-3 rounded-lg bg-emerald-900/20 border border-emerald-800/30 text-emerald-100 placeholder-emerald-300/60 focus:outline-none focus:border-emerald-600 resize-none"
                        rows={3}
                      />
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => setIsCommentGifPickerOpen(!isCommentGifPickerOpen)}
                            className="p-2 rounded-lg hover:bg-emerald-800/30 transition-colors text-emerald-300/80 hover:text-emerald-200"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m0 0V1h-1m1 3h4a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2h4m0-3v3m0 0h8" />
                            </svg>
                          </button>
                        </div>
                        
                        <button
                          type="submit"
                          disabled={(!commentText.trim() && !selectedCommentGif) || isCommenting}
                          className="cosmic-button text-sm disabled:opacity-50"
                        >
                          {isCommenting ? 'Publication...' : 'Commenter'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* Liste des commentaires */}
          <div className="space-y-4">
            {isLoadingComments ? (
              <div className="text-center text-emerald-300/60 py-4">
                Chargement des commentaires...
              </div>
            ) : (
              comments.map(comment => renderComment(comment))
            )}
          </div>
        </div>
      </div>

      {/* Modal d'image en plein écran */}
      {selectedImageIndex !== null && post?.mediaUrls && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4"
          onClick={() => setSelectedImageIndex(null)}
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            zIndex: 9999,
            margin: 0
          }}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img
              src={post.mediaUrls[selectedImageIndex]}
              alt={`Post media ${selectedImageIndex + 1}`}
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setSelectedImageIndex(null)}
              className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full w-8 h-8 flex items-center justify-center hover:bg-opacity-70"
            >
              ×
            </button>
            {post.mediaUrls.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImageIndex(prev => 
                      prev === null ? 0 : prev > 0 ? prev - 1 : post.mediaUrls!.length - 1
                    );
                  }}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-70"
                >
                  ‹
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImageIndex(prev => 
                      prev === null ? 0 : prev < post.mediaUrls!.length - 1 ? prev + 1 : 0
                    );
                  }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-70"
                >
                  ›
                </button>
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* GIF Picker pour les commentaires */}
      {isCommentGifPickerOpen && (
        <GifPicker
          isOpen={isCommentGifPickerOpen}
          onClose={() => setIsCommentGifPickerOpen(false)}
          onSelectGif={handleCommentGifSelect}
        />
      )}

      {/* GIF Picker pour les réponses */}
      {isReplyGifPickerOpen && (
        <GifPicker
          isOpen={isReplyGifPickerOpen}
          onClose={() => setIsReplyGifPickerOpen(false)}
          onSelectGif={handleReplyGifSelect}
        />
      )}
    </div>
  );
};
